import React, { useEffect, useMemo, useState } from "react";
import { apiClient, API_BASE_URL } from "../services/api";
import { lawyerInfoService } from "../services/lawyerInfoService";
import { RepresentativeInfo } from "../types/lawyer";
import "./PublicConsultationForm.css";

interface PublicConsultationFormProps {
  linkId: string;
  template: {
    id: string;
    template_name: string;
    template_description: string;
    form_fields: {
      fields: FormField[];
    };
  };
  practiceArea: string;
  country?: string | null;
  visaType?: string | null;
  caseType?: string | null;
  formId?: string | null;
  initialValues?: Record<string, any> | null;
  clientContext?: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

interface TableColumn {
  name: string;
  label: string;
  type: string;
  placeholder?: string;
}

interface FormField {
  name: string;
  label: string;
  type: string;
  required: boolean;
  placeholder?: string;
  options?: string[];
  conditional?: string;
  conditional_value?: string;
  conditional_values?: string[];
  columns?: TableColumn[];
  fullWidth?: boolean;
}

interface SubmissionState {
  status: "idle" | "submitting" | "success" | "error";
  message?: string;
}

const PublicConsultationForm: React.FC<PublicConsultationFormProps> = ({
  linkId,
  template,
  practiceArea,
  country,
  visaType,
  caseType,
  formId,
  initialValues,
  clientContext,
}) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [submission, setSubmission] = useState<SubmissionState>({
    status: "idle",
  });
  const [lawyerInfo, setLawyerInfo] = useState<RepresentativeInfo | null>(null);
  const [loadingLawyerInfo, setLoadingLawyerInfo] = useState<boolean>(false);

  useEffect(() => {
    if (!template?.form_fields?.fields?.length) {
      return;
    }

    const defaults: Record<string, any> = {};

    template.form_fields.fields.forEach((field) => {
      if (
        initialValues &&
        Object.prototype.hasOwnProperty.call(initialValues, field.name)
      ) {
        defaults[field.name] = initialValues[field.name];
      } else {
        defaults[field.name] = field.type === "checkbox" ? [] : "";
      }
    });

    if (initialValues) {
      Object.keys(initialValues).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(defaults, key)) {
          defaults[key] = initialValues[key];
        }
      });
    }

    setFormValues(defaults);
  }, [template, initialValues]);

  useEffect(() => {
    const loadLawyerInfo = async () => {
      if (!linkId) return;
      try {
        setLoadingLawyerInfo(true);
        const linkResponse = await fetch(`${API_BASE_URL}/firm-link/${linkId}`);
        if (!linkResponse.ok) {
          return;
        }
        const linkData = await linkResponse.json();
        const lawyerId = linkData?.lawyerId;
        if (!lawyerId) {
          return;
        }
        const { data, error } = await lawyerInfoService.getLawyerInfoForForms(
          lawyerId
        );
        if (!error && data) {
          setLawyerInfo(data as RepresentativeInfo);
        }
      } catch (error) {
        console.error("❌ Failed to fetch lawyer info:", error);
      } finally {
        setLoadingLawyerInfo(false);
      }
    };

    loadLawyerInfo();
  }, [linkId]);

  const visibleFields = useMemo(() => {
    if (!template?.form_fields?.fields) {
      return [];
    }
    return template.form_fields.fields.filter((field) => {
      if (!field.conditional && !field.conditional_values) {
        return true;
      }

      const trigger = field.conditional;
      if (!trigger) {
        return true;
      }

      const triggerValue = formValues[trigger];
      if (field.conditional_values && field.conditional_values.length > 0) {
        return field.conditional_values.includes(triggerValue);
      }

      if (field.conditional_value) {
        return triggerValue === field.conditional_value;
      }

      return true;
    });
  }, [template, formValues]);

  const handleInputChange = (name: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formId && !linkId) {
      setSubmission({
        status: "error",
        message: "Invalid intake link. Please verify the URL.",
      });
      return;
    }

    setSubmission({ status: "submitting" });

    try {
      if (formId) {
        const response = await apiClient.publicSubmitExistingForm(
          formId,
          formValues
        );
        setSubmission({
          status: "success",
          message:
            response?.message ||
            "Thank you! Your information has been submitted.",
        });
      } else {
        await apiClient.publicSubmitFirmLink(linkId, {
          templateId: template.id,
          practiceArea,
          country,
          visaType,
          caseType,
          clientName:
            formValues.client_name ||
            formValues.full_name ||
            formValues.tenant_name ||
            "Client",
          clientEmail:
            formValues.client_email ||
            formValues.email ||
            formValues.tenant_email ||
            "",
          clientPhone:
            formValues.client_phone ||
            formValues.phone ||
            formValues.tenant_phone ||
            "",
          formTitle: template.template_name,
          formData: formValues,
        });

        setSubmission({
          status: "success",
          message: "Thank you! Your information has been submitted.",
        });
      }
    } catch (error) {
      console.error("❌ Public consultation submission failed:", error);
      setSubmission({
        status: "error",
        message:
          error instanceof Error
            ? error.message
            : "Submission failed. Please try again.",
      });
    }
  };

  if (!template) {
    return (
      <div className="pcf-page">
        <div className="pcf-message error">
          <p>Consultation form template is not available.</p>
        </div>
      </div>
    );
  }

  if (submission.status === "success") {
    return (
      <div className="pcf-page pcf-page-success">
        <div className="pcf-message success">
          <h2>Submission Received</h2>
          <p>{submission.message}</p>
          <p>You will receive a confirmation email soon.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pcf-page">
      <div className="pcf-hero">
        <div className="pcf-hero-card">
          <h1>{template.template_name}</h1>
          {template.template_description && (
            <p className="pcf-hero-description">
              {template.template_description}
            </p>
          )}
        </div>
      </div>

      <div className="pcf-body">
        {clientContext &&
          (clientContext.name ||
            clientContext.email ||
            clientContext.phone) && (
            <div className="pcf-client-card">
              <h3>Client Information</h3>
              <div className="pcf-client-info-grid">
                {clientContext.name && (
                  <div className="pcf-client-info-item">
                    <span className="pcf-client-label">Client</span>
                    <span className="pcf-client-value">
                      {clientContext.name}
                    </span>
                  </div>
                )}
                {clientContext.email && (
                  <div className="pcf-client-info-item">
                    <span className="pcf-client-label">Email</span>
                    <span className="pcf-client-value">
                      {clientContext.email}
                    </span>
                  </div>
                )}
                {clientContext.phone && (
                  <div className="pcf-client-info-item">
                    <span className="pcf-client-label">Phone</span>
                    <span className="pcf-client-value">
                      {clientContext.phone}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

        {lawyerInfo && !loadingLawyerInfo && (
          <div className="pcf-info-card">
            <div className="pcf-info-header">
              <div className="pcf-info-header-left">
                {lawyerInfo.profileImageUrl && (
                  <img
                    src={lawyerInfo.profileImageUrl}
                    alt="Representative profile"
                    className="pcf-rep-photo"
                  />
                )}
                <h3>Representative Information:</h3>
              </div>
              {lawyerInfo.firmLogoUrl && (
                <img
                  src={lawyerInfo.firmLogoUrl}
                  alt="Firm logo"
                  className="pcf-firm-logo"
                />
              )}
            </div>
            <div className="pcf-info-grid">
              <div>
                <span className="pcf-info-label">Name:</span>
                <span className="pcf-info-value">
                  {lawyerInfo.firstName} {lawyerInfo.lastName}
                </span>
              </div>
              <div>
                <span className="pcf-info-label">Firm:</span>
                <span className="pcf-info-value">
                  {lawyerInfo.lawFirm || "—"}
                </span>
              </div>
              <div>
                <span className="pcf-info-label">Email:</span>
                <span className="pcf-info-value">
                  {lawyerInfo.email || "—"}
                </span>
              </div>
              <div>
                <span className="pcf-info-label">Phone:</span>
                <span className="pcf-info-value">
                  {formatPhone(
                    lawyerInfo.phoneCountryCode,
                    lawyerInfo.phoneNumber
                  )}
                </span>
              </div>
              <div className="pcf-info-full">
                <span className="pcf-info-label">Address:</span>
                <span className="pcf-info-value">
                  {formatAddress(lawyerInfo.address)}
                </span>
              </div>
            </div>
          </div>
        )}

        {submission.status === "error" && (
          <div className="pcf-message error">
            <p>{submission.message}</p>
          </div>
        )}

        <div className="pcf-form-card">
          <form onSubmit={handleSubmit} className="pcf-form-grid">
            {visibleFields.map((field) => (
              <FormFieldRenderer
                key={field.name}
                field={field}
                value={formValues[field.name]}
                onChange={handleInputChange}
              />
            ))}

            <div className="pcf-submit-row">
              <p className="pcf-terms">
                By submitting this form, you agree to our{" "}
                <a
                  href="/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
                .
              </p>
              <button
                type="submit"
                className="pcf-submit"
                disabled={submission.status === "submitting"}
              >
                {submission.status === "submitting"
                  ? "Submitting…"
                  : "Submit Consultation"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

interface FieldRendererProps {
  field: FormField;
  value: any;
  onChange: (name: string, value: any) => void;
}

const FormFieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  value,
  onChange,
}) => {
  const handleChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const target = event.target;
    const { name, value: inputValue } = target;

    if (target instanceof HTMLInputElement && target.type === "checkbox") {
      const current = Array.isArray(value) ? value : [];
      if (target.checked) {
        onChange(name, [...current, inputValue]);
      } else {
        onChange(
          name,
          current.filter((item: string) => item !== inputValue)
        );
      }
      return;
    }

    onChange(name, inputValue);
  };

  switch (field.type) {
    case "textarea":
      return (
        <div
          className={`pcf-field ${
            field.type === "textarea" ? "full-width" : ""
          }`}
        >
          <label htmlFor={field.name}>
            {field.label}
            {field.required && " *"}
          </label>
          <textarea
            id={field.name}
            name={field.name}
            required={field.required}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
          />
        </div>
      );

    case "select":
      return (
        <div className="pcf-field">
          <label htmlFor={field.name}>
            {field.label}
            {field.required && " *"}
          </label>
          <select
            id={field.name}
            name={field.name}
            required={field.required}
            value={value || ""}
            onChange={handleChange}
          >
            <option value="">Select an option</option>
            {field.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      );

    case "checkbox":
      return (
        <div className={`pcf-field ${field.fullWidth ? "full-width" : ""}`}>
          <label>
            {field.label}
            {field.required && " *"}
          </label>
          <div className="pcf-checkbox-group">
            {field.options?.map((option) => (
              <label key={option} className="pcf-checkbox">
                <input
                  type="checkbox"
                  name={field.name}
                  value={option}
                  checked={
                    Array.isArray(value) ? value.includes(option) : false
                  }
                  onChange={handleChange}
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      );

    case "education_table":
    case "work_table":
    case "ielts_table":
      return (
        <div className="pcf-field full-width table-field">
          <label>
            {field.label}
            {field.required && " *"}
          </label>
          <TableFieldRenderer
            field={field}
            value={value || {}}
            onChange={onChange}
          />
        </div>
      );

    default:
      return (
        <div className="pcf-field">
          <label htmlFor={field.name}>
            {field.label}
            {field.required && " *"}
          </label>
          <input
            id={field.name}
            name={field.name}
            type={field.type}
            required={field.required}
            placeholder={field.placeholder}
            value={value || ""}
            onChange={handleChange}
          />
        </div>
      );
  }
};

interface TableRendererProps {
  field: FormField;
  value: Record<string, any>;
  onChange: (name: string, value: any) => void;
}

const TableFieldRenderer: React.FC<TableRendererProps> = ({
  field,
  value,
  onChange,
}) => {
  const columns = field.columns || [];

  const handleColumnChange = (column: TableColumn, columnValue: string) => {
    onChange(field.name, {
      ...value,
      [column.name]: columnValue,
    });
  };

  return (
    <div className="pcf-table">
      <div className="pcf-table-scroll">
        <div className="pcf-table-header">
          {columns.map((column: TableColumn) => (
            <div key={column.name} className="pcf-table-cell">
              {column.label}
            </div>
          ))}
        </div>
        <div className="pcf-table-row">
          {columns.map((column: TableColumn) => (
            <div key={column.name} className="pcf-table-cell">
              <input
                type="text"
                value={value?.[column.name] || ""}
                placeholder={column.placeholder || ""}
                onChange={(event) =>
                  handleColumnChange(column, event.target.value)
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

function formatPhone(countryCode: string, number: string): string {
  if (!countryCode && !number) {
    return "—";
  }
  const formattedNumber = number
    ? number.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")
    : "";
  return `${countryCode ? `+${countryCode} ` : ""}${
    formattedNumber || number || "—"
  }`.trim();
}

function formatAddress(address?: RepresentativeInfo["address"]): string {
  if (!address) {
    return "—";
  }

  const parts = [
    [address.streetNumber, address.streetName, address.aptNumber]
      .filter(Boolean)
      .join(" "),
    [address.city, address.province].filter(Boolean).join(", "),
    [address.country, address.postalCode].filter(Boolean).join(" "),
  ].filter(Boolean);

  return parts.join(", ");
}

export default PublicConsultationForm;
